'use client';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H2, H3, H4, P } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';

import image from '../assets/mo-pointing.png';
import osmosisAIImage from '../assets/osmosis-ai.png';
import { Route } from '../hooks/useRouterPush';
import { getAuthPath } from '../lib/composer/url';
import { text3, textBlue } from '@osmosis/tailwind-ui/styles/text';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { LockoutModalVariant } from '../constants/lockout';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { useEmbedContext } from '@osmosis/nextjs-components/context';
import { track } from '../lib/util/tracking';
import TrialCTA from './TrialCTA';
import type { TrialCtaArgs } from '../lib/composer/schemas/answersValueProp';

interface LockoutModalContentProps {
  onClose?: () => void;
  closeButton?: boolean;
  dataTestId: string;
  quizHeader?: boolean;
  headerMessage?: React.ReactNode;
  bodyMessage?: React.ReactNode;
  variant?: LockoutModalVariant;
  trialCta?: TrialCtaArgs;
}

type MainContentProps = {
  signUpLoading: boolean;
  dataTestId: string;
  pathName: string | null;
  searchParams: URLSearchParams | null;
  headerMessage?: React.ReactNode;
  bodyMessage?: React.ReactNode;
  variant?: LockoutModalVariant;
  trialCta?: TrialCtaArgs;
};

const MainContent = ({
  dataTestId,
  pathName,
  searchParams,
  headerMessage = (
    <FormattedMessage
      id="answers.lockout-modal.heading"
      defaultMessage="Start learning instantly — free for 7 days"
    />
  ),
  bodyMessage,
  variant = LockoutModalVariant.DEFAULT,
  trialCta,
}: MainContentProps) => {
  const [signInLoading, setSignInLoading] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);

  useEffect(() => () => setSignInLoading(false), []);

  const { targetAttribute } = useEmbedContext();

  const isAIVariant = [
    LockoutModalVariant.DEFAULT_AI,
    LockoutModalVariant.EXPIRED_PRIME_AI,
  ].includes(variant);
  const isExpiredPrime =
    variant === LockoutModalVariant.EXPIRED_PRIME ||
    variant === LockoutModalVariant.EXPIRED_PRIME_AI;

  const imageToShow = isAIVariant ? osmosisAIImage : image;

  return (
    <>
      <Box
        className="mt-16 text-center"
        data-testid={`${dataTestId}-main-content`}
      >
        <Image
          alt="Create your free Osmosis account to unlock this content"
          src={imageToShow}
          width={isAIVariant ? 64 : 180}
          height={isAIVariant ? 64 : 172}
        />
      </Box>
      {isAIVariant ? (
        <H2
          className="bg-clip-text !text-2xl font-semibold text-transparent"
          style={{
            backgroundImage:
              'radial-gradient(88.19% 88.19% at 6.3% 60.61%, #2F41EA 0%, #F740A7 50.03%, #FF6F21 100%)',
          }}
        >
          <FormattedMessage
            id="osmosis-ai-btn.label"
            defaultMessage="Ask Osmosis AI"
          />
        </H2>
      ) : null}
      <Box className="pl-6 pr-6 text-center">
        <H2 className="mb-7 mt-9 max-w-md text-2xl">
          {variant === LockoutModalVariant.QUIZ ? (
            <FormattedMessage
              id="quiz.lockout-modal.heading"
              defaultMessage="Keep going with your quiz after sign up — free for 7 days"
            />
          ) : (
            headerMessage
          )}
        </H2>
        {isExpiredPrime && (
          <P className="-mt-5 mb-3 font-bold text-red-800">
            <FormattedMessage
              id="lockout-modal.expired"
              defaultMessage="Your account access has expired."
            />
          </P>
        )}
        <H3
          className={cn(
            `max-w-md mb-6 !text-base`,
            text3,
            isAIVariant ? 'mb-10' : 'mb-6',
          )}
        >
          {bodyMessage || (
            <FormattedMessage
              id="answers.lockout-modal.text1"
              defaultMessage="Create a free account to keep viewing this content and explore our full library and study tools."
            />
          )}
        </H3>
      </Box>
      {isExpiredPrime ? (
        <Box className="mb-8">
          <a href={'/plans'} target={targetAttribute}>
            <Button
              data-testid="lockout.view-plans.btn"
              className="outline-hidden rounded-full"
              endIcon={<Icon icon={faArrowRight} />}
            >
              <FormattedMessage
                id="answers.lockout-modal.view-plans-btn"
                defaultMessage="View plans"
              />
            </Button>
          </a>
        </Box>
      ) : (
        <>
          <Flex className="mb-8 flex-col items-center justify-center gap-2">
            {!isAIVariant && trialCta !== undefined ? (
              <TrialCTA
                copy={trialCta}
                target={targetAttribute}
                isLoading={signUpLoading}
                onButtonClick={() => {
                  track(
                    'begin_registration',
                    { source: 'lockout_modal', variant },
                    ['gtm'],
                  );
                  setSignUpLoading(true);
                }}
                containerTestId="answers-lockout-trial-cta"
                buttonTestId="answers.sign-up.btn"
                fineprintTestId="answers-lockout-trial-cta-fineprint"
                classNames={{ container: 'rounded-lg' }}
              />
            ) : (
              <>
                <a
                  tabIndex={-1}
                  target={targetAttribute}
                  href={getAuthPath(Route.CREATE, pathName, searchParams)}
                >
                  <Button
                    data-testid="answers.sign-up.btn"
                    className="outline-hidden"
                    isLoading={signUpLoading}
                    onClick={() => {
                      track(
                        'begin_registration',
                        { source: 'lockout_modal', variant },
                        ['gtm'],
                      );
                      setSignUpLoading(true);
                    }}
                    rounded
                  >
                    {isAIVariant ? (
                      <FormattedMessage
                        id="auth.sign_up"
                        defaultMessage="Create a free account"
                      />
                    ) : (
                      <FormattedMessage
                        id="answers.lockout-modal.create-account-btn"
                        defaultMessage="Start free trial"
                      />
                    )}
                  </Button>
                </a>

                {isAIVariant ? null : (
                  <H4 className={`text-center text-xs ${text3}`}>
                    <FormattedMessage
                      id="answers.lockout-modal.sign-up-note"
                      defaultMessage="No credit card required."
                    />
                  </H4>
                )}
              </>
            )}
          </Flex>

          <Flex className="mb-14 items-center justify-center gap-2">
            <H4 className={`${text3} inline-block`}>
              <FormattedMessage
                id="login.account"
                defaultMessage="Already have an account?"
              />
            </H4>
            <a
              href={getAuthPath(Route.LOGIN, pathName, searchParams)}
              target={targetAttribute}
            >
              <Button
                data-testid="answers.log-in.btn"
                variant="transparent"
                className="px-1 py-2 hover:bg-transparent active:bg-transparent"
                isLoading={signInLoading}
                onClick={() => setSignInLoading(true)}
              >
                <H4
                  className={`${textBlue} underline decoration-inherit decoration-solid`}
                >
                  <FormattedMessage
                    id="common.signIn"
                    defaultMessage="Sign in"
                  />
                </H4>
              </Button>
            </a>
          </Flex>
        </>
      )}
    </>
  );
};

/**
 * AuthPromptModalContent.
 */
const LockoutModalContent: React.FC<LockoutModalContentProps> = ({
  onClose,
  closeButton = false,
  dataTestId,
  headerMessage,
  bodyMessage,
  variant,
  trialCta,
}) => {
  const [signUpLoading, setSignUpLoading] = useState(false);

  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { formatMessage } = useIntl();

  useEffect(() => () => setSignUpLoading(false), []);

  const closeButtonLabel = formatMessage({
    id: 'common.close',
    defaultMessage: 'Close',
  });

  return (
    <Flex
      className={`relative w-full max-w-xl flex-col items-center ${bg0} rounded-lg`}
    >
      {closeButton && onClose && (
        <IconButton
          data-testid="close_button"
          variant="transparent"
          icon={<Icon icon={faTimes} />}
          className={`absolute right-4 top-4 z-10 bg-transparent text-base hover:bg-transparent`}
          aria-label={closeButtonLabel}
          onClick={onClose}
        />
      )}
      {
        <MainContent
          signUpLoading={signUpLoading}
          dataTestId={dataTestId}
          headerMessage={headerMessage}
          bodyMessage={bodyMessage}
          pathName={pathName}
          searchParams={searchParams}
          variant={variant}
          trialCta={trialCta}
        />
      }
    </Flex>
  );
};

export default LockoutModalContent;
